import React from "react";
import { motion } from "framer-motion";
import { Check, Truck, MapPin, Package, XCircle } from "lucide-react";

// Two visual pipelines depending on fulfillment method.
const STEPS_SHIP = ["paid", "prepping", "shipped", "fulfilled"];
const STEPS_PICKUP = ["paid", "prepping", "ready-for-pickup", "fulfilled"];

const LABEL = {
  paid: "Confirmed",
  prepping: "Prepping",
  shipped: "Shipped",
  "ready-for-pickup": "Ready for pickup",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
};

const ICON = {
  paid: Check,
  prepping: Package,
  shipped: Truck,
  "ready-for-pickup": MapPin,
  fulfilled: Check,
};

export default function OrderTimeline({ order }) {
  const isPickup = order.fulfillment === "pickup";
  const steps = isPickup ? STEPS_PICKUP : STEPS_SHIP;
  // Treat legacy "confirmed" status as "paid" for the timeline
  const normalized = order.status === "confirmed" ? "paid" : (order.status || "paid");
  const currentIndex = steps.indexOf(normalized);
  const cancelled = order.status === "cancelled";

  if (cancelled) {
    return (
      <div className="mt-4 flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-[#8B2E4F]">
        <XCircle size={14} strokeWidth={1.5} /> Cancelled
      </div>
    );
  }

  return (
    <div
      className="mt-6 pb-2"
      data-testid={`order-timeline-${order.id}`}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={steps.length}
      aria-valuenow={Math.max(0, currentIndex + 1)}
    >
      <div className="relative flex items-start justify-between gap-2">
        {/* Connector line — filled up to current index */}
        <div className="absolute left-3 right-3 top-3 h-px bg-[color:var(--sr-border-light)]" />
        <motion.div
          initial={{ width: "0%" }}
          animate={{
            width:
              currentIndex <= 0
                ? "0%"
                : `${(currentIndex / (steps.length - 1)) * 100}%`,
          }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute left-3 top-3 h-px bg-[color:var(--sr-primary)] origin-left"
          style={{ maxWidth: "calc(100% - 24px)" }}
        />
        {steps.map((step, idx) => {
          const done = idx <= currentIndex;
          const active = idx === currentIndex;
          const Icon = ICON[step];
          return (
            <div
              key={step}
              className="relative flex flex-col items-center gap-2 z-10"
              style={{ width: `${100 / steps.length}%` }}
            >
              <motion.div
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: idx * 0.08, duration: 0.5 }}
                className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${
                  done
                    ? active
                      ? "bg-[color:var(--sr-accent)] border-[color:var(--sr-accent)] text-[color:var(--sr-bg)]"
                      : "bg-[color:var(--sr-primary)] border-[color:var(--sr-primary)] text-[color:var(--sr-secondary)]"
                    : "bg-[color:var(--sr-bg)] hairline text-[color:var(--sr-muted)]"
                }`}
              >
                <Icon size={12} strokeWidth={1.75} />
              </motion.div>
              <p
                className={`text-[10px] tracking-[0.18em] uppercase text-center leading-tight ${
                  done ? "text-[color:var(--sr-primary)]" : "text-[color:var(--sr-muted)]"
                }`}
              >
                {LABEL[step]}
              </p>
              {active && (
                <motion.span
                  layoutId={`pulse-${order.id}`}
                  className="absolute -top-1 w-8 h-8 rounded-full bg-[color:var(--sr-accent)]/25 animate-pulse"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
